import * as React from 'react';
import { bindActionCreators, Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Dimmer, Loader, Portal } from 'semantic-ui-react';
import {
    ReflexContainer,
    ReflexSplitter,
    ReflexElement,
} from 'react-reflex';
import 'react-reflex/styles.css';
import { History } from 'history';
import DOMPurify from 'dompurify';

import commonWorkspaceSchemeService from 'common/services/commonWorkspaceSchemeService';
import FeatureWrapper from 'common/features/FeatureWrapper';
import { WorkspacesState } from 'common/store/workspaces/reducer';
import * as workspaceActions from 'common/store/workspaces/actions';
import { PRINT_ANNOTATIONS_ONLY_WORKSPACE, WorkspacesOverlayTargets } from 'common/store/workspaces/types';
import workspaceParser from 'common/services/workspaceParser';
import { tl } from 'common/services/i18nService';
import { FeatureNamespace, getFeatureNamespace } from 'common/services/featureService';
import FeatureLoader from 'common/features/FeatureLoader';
import { AlertsState } from 'common/components/notifications/reducer';
import { Alert, AlertLevel } from 'common/components/notifications/types';
import * as alertActions from 'common/components/notifications/actions';
import * as sessionActions from 'common/store/session/actions';
import * as styles from 'common/components/App.scss';
import { TabItem } from 'common/types/TabTypes';
import urlUtil from 'common/util/url/urlUtil';
import WorkspaceOverlay from 'common/components/workspaceItem/WorkspaceOverlay';
import FeatureHeader from 'common/features/FeatureHeader';
import NotificationDisplay from 'common/components/notifications/NotificationDisplay';
import { CommonRootState } from 'common/store/typings';
import { SessionState, ExtendedSessionState } from 'common/store/session/reducer';
import ErrorPage from 'common/components/errorPage/ErrorPage';
import { isNullOrUndefined } from 'common/util/utils';
import { isFocusView, isPrintViewParams } from 'common/util/shared/sharedUtils';
import ImpersonationBanner from 'common/components/impersonation/ImpersonationBanner';
import DevEnvironmentConfig from './devEnvironmentConfig/DevEnvironmentConfig';
import Header from './header/Header';
import { ReloadState } from './reload/reducer';
import ReloadPage from './reload/ReloadPage';
import { ReloadMessage } from './reload/actions';
import ModalDialog from '../store/modal/ModalDialog';
import AiSupportedMarkingIntroModal from '../features/aiSupportedMarkingIntro/components/AiSupportedMarkingIntroModal';
import { isAiMarkingViewTab, shouldEmbedAiMarking } from '../features/aiSupportedMarkingIntro/guard';
import AiMarkingFullWindow from '../features/submission/components/AiMarkingFullWindow';
import { OptionsMenuState } from '../store/optionsMenu/reducer';
import gradingAppStore from '../store/index';

import { ContextState } from '../store/context/reducer';
import { AssessmentState, GradeType } from '../store/assessment/reducer';
import { RootState } from '../store/rootReducer';
import { recordingService } from '../features/recordingHeader/recordingService';
import actionStatusService from '../services/actionStatusService';
import urlContextParametersService from '../services/urlContextParametersService';
import { getGradeTranslatedLabel } from '../util/gradingUtils';
import Sidebar from './sideBar/Sidebar';
import navigationListService from '../services/navigationListService';
import { getCandidateCountBySelectedCommitteeId } from '../services/committeeService';
import { userIsStudent } from '../services/permissions/PermissionService';
import WarningBar from './warningBar/WarningBar';

import apiService from '../services/api/apiService';


interface StateProps {
    context: ContextState;
    session: SessionState;
    workspaces: WorkspacesState;
    assessment: AssessmentState;
    reload: ReloadState;
    alerts: AlertsState;
    optionsMenu: OptionsMenuState;
    shouldShowRecordingHeader: boolean;
}
interface HistoryProps {
    history: History;
}
interface AppProps extends StateProps, HistoryProps{
    onFetchSignedWebSocketUrl: () => void;
    onFeatureLoadComplete: (featureId: string) => void;
    onSetupContextReducer?: (featureId: string) => void;
    onCandidateFilterChange: (item: TabItem) => void;
    onExitImpersonation: () => void;
    onSetSession: (sessionData: ExtendedSessionState) => void;
    onPushAlert: (alert: Alert, notRemove?: boolean) => void;
}

interface ReflexColumn {
    flex: number;
}

interface ReflexLayout {
    [columnKey: string]: ReflexColumn;
}

interface State {
    reflexLayout: ReflexLayout;
}

class AppComponent extends React.PureComponent<AppProps, State> {
    constructor(props: AppProps) {
        super(props);
        const { context } = this.props;
        const savedLayout = JSON.parse(localStorage.getItem(
            this.getReflexElementLocalStorageKey(context.assessmentRunId, context.partyId, context.workspaceId),
        ));
        this.state = {
            reflexLayout: savedLayout || {},
        };
    }

    componentDidMount() {
        const impersonationDataString = sessionStorage.getItem('impersonationData');
        if (impersonationDataString) {
            const impersonationData = JSON.parse(impersonationDataString);
            if (impersonationData.isImpersonating) {
                const sessionData: ExtendedSessionState = {
                    ...this.props.session,
                    user: impersonationData.impersonatedUser,
                    originalUser: impersonationData.impersonatorUserId,
                    impersonationType: impersonationData.impersonationType,
                };
                this.props.onSetSession(sessionData);
            }
        }
        this.props.onFetchSignedWebSocketUrl();
    }

    getDevEnvironmentConfig = () => {
        if (process.env.NODE_ENV === 'development' && DEV_PICK_ENV && window.location.href.indexOf('/url') > 0) {
            return <DevEnvironmentConfig />;
        }
        return null;
    };

    onReflexElementResize = (handleProps: any) => {
        const { context } = this.props;
        const { className, flex } = handleProps.component.props;
        const { reflexLayout } = this.state;

        this.setState(
            {
                reflexLayout: {
                    ...reflexLayout,
                    [className]: { flex },
                },
            },
            () => {
                const key: string = this.getReflexElementLocalStorageKey(
                    context.assessmentRunId,
                    context.partyId,
                    context.workspaceId,
                );
                localStorage.setItem(key, JSON.stringify(this.state.reflexLayout));
            }
        );
    };

    getReflexElementLocalStorageKey(assessmentRunId: number, partyId: number, workspace: string): string {
        return `workspace_column_width_${assessmentRunId}_${partyId}_${workspace}`;
    }

    getFlexForColumnFromLayout(columnKey: string): number {
        if (this.state.reflexLayout && this.state.reflexLayout[columnKey] && !isNaN(this.state.reflexLayout[columnKey].flex)) {
            return this.state.reflexLayout[columnKey].flex;
        }
        return null;
    }

    parseFeatures = (aFeatureIsExpanded: boolean): React.ReactElement[] => {
        const {
            workspaces, context, onSetupContextReducer, onFeatureLoadComplete,
            session, assessment,
        } = this.props;
        const activeOverlay = workspaces && workspaces.activeOverlay;
        const mainFeatures = workspaces.currentWorkspace.main;
        const { layout } = workspaces.currentWorkspace;
        const numberOfColumns: number = mainFeatures.filter((mainFeature: any) => {
            return mainFeature.features.length > 0 && (context.showDetailedView || !mainFeature.features[0].detailedViewFeature);
        }).length;

        // AI-supported questions are marked inside the Graide iframe, so hide the side columns
        const showAiEmbed: boolean = shouldEmbedAiMarking(context);

        const mainFeaturesElems: any = [];
        mainFeatures.forEach((featureGroup: any, index: number) => {
            const columnKey = `${context.workspaceId}_column${index}`;

            if (!featureGroup.features.length) {
                return;
            }

            if (!context.showDetailedView && featureGroup.features[0].detailedViewFeature) {
                return;
            }

            if (showAiEmbed && index > 0) {
                return;
            }

            const columnFlex: number = this.getFlexForColumnFromLayout(columnKey);
            mainFeaturesElems.push(
                <ReflexElement
                    key={columnKey}
                    className={columnKey}
                    flex={columnFlex || layout[index]}
                    onStopResize={this.onReflexElementResize}
                    propagateDimensions
                >
                    <FeatureWrapper className="column">
                        {
                            featureGroup.features.map((feature: any, key: number) => {
                                const featureId = `feature_${feature.type}_${context.workspaceId}_${index}_${key}`;
                                const featureNamespace = getFeatureNamespace(feature.type, FeatureNamespace.GRADING);
                                return (
                                    <FeatureLoader
                                        FeatureHeader={FeatureHeader}
                                        key={featureId}
                                        setupGenericFeatureState={onSetupContextReducer}
                                        getGenericFeatureState={this.onGetGenericFeatureState}
                                        featureName={feature.type}
                                        isFeatureLoading={this.isFeatureLoading}
                                        options={feature.options}
                                        setFeatureLoaded={onFeatureLoadComplete}
                                        context={context}
                                        footerMode={false}
                                        featureId={featureId}
                                        featureIsExpandedMode={aFeatureIsExpanded}
                                        usePlagiarismService={assessment.usePlagiarismService}
                                        language={session?.language}
                                        activeOverlay={activeOverlay}
                                        workspaces={workspaces}
                                        appStore={gradingAppStore.getStore()}
                                        featureNamespace={featureNamespace}
                                        actionStatusService={actionStatusService}
                                    />
                                );
                            })
                        }
                    </FeatureWrapper>
                </ReflexElement>
            );
            // No splitter in AI-supported marking mode: the embed takes the full width.
            if (!showAiEmbed && numberOfColumns > 1 && index < mainFeatures.length - 1) {
                mainFeaturesElems.push(<ReflexSplitter key={`${columnKey}_splitter`} propagate />);
            }
        });
        return mainFeaturesElems;
    };

    renderOnlyExpandedFeature = (featureId: string) => {
        const {
            workspaces, context, onSetupContextReducer, onFeatureLoadComplete, session,
            assessment,
        } = this.props;
        const activeOverlay = workspaces && workspaces.activeOverlay;
        const type = featureId.split('_')[1];
        const featureConfig = workspaceParser.getFeatureConfig(workspaces.currentWorkspace.main, type);
        const featureNamespace = getFeatureNamespace(type, FeatureNamespace.GRADING);

        return (
            <div className="column column--flex-12 no-shrink">
                <FeatureLoader
                    FeatureHeader={FeatureHeader}
                    key={featureId}
                    setupGenericFeatureState={onSetupContextReducer}
                    getGenericFeatureState={this.onGetGenericFeatureState}
                    featureName={type}
                    useFullScreen
                    isFeatureLoading={this.isFeatureLoading}
                    options={featureConfig.options}
                    setFeatureLoaded={onFeatureLoadComplete}
                    context={context}
                    footerMode={false}
                    featureId={featureId}
                    usePlagiarismService={assessment.usePlagiarismService}
                    language={session?.language}
                    activeOverlay={activeOverlay}
                    workspaces={workspaces}
                    appStore={gradingAppStore.getStore()}
                    featureNamespace={featureNamespace}
                    actionStatusService={actionStatusService}
                />
            </div>
        );
    };

    renderExplanationRecordingHeaderFeature = () => {
        const {
            workspaces, context, onSetupContextReducer, onFeatureLoadComplete, session,
        } = this.props;
        const activeOverlay = workspaces && workspaces.activeOverlay;
        const featureId = 'recordingHeader';

        return (
            <div className="">
                <FeatureLoader
                    FeatureHeader={FeatureHeader}
                    setupGenericFeatureState={onSetupContextReducer}
                    getGenericFeatureState={this.onGetGenericFeatureState}
                    isFeatureLoading={this.isFeatureLoading}
                    setFeatureLoaded={onFeatureLoadComplete}
                    featureName={featureId}
                    options={null}
                    context={context}
                    featureId={featureId}
                    workspaces={workspaces}
                    language={session?.language}
                    activeOverlay={activeOverlay}
                    appStore={gradingAppStore.getStore()}
                    featureNamespace={getFeatureNamespace(featureId, FeatureNamespace.GRADING)}
                    actionStatusService={actionStatusService}
                />
            </div>
        );
    };

    isFeatureLoading = (featureId: string): boolean => {
        const { featuresLoading } = this.props.workspaces;
        return featuresLoading.some((feature) => feature === featureId);
    };

    onGetGenericFeatureState = (featureId: string) => {
        const { genericFeatureState } = this.props.workspaces;

        return genericFeatureState[featureId] || genericFeatureState.default;
    };

    renderLoader = (): React.ReactElement => {
        return (
            <Dimmer active inverted>
                <Loader inverted>
                    {tl('grading.loading')}
                </Loader>
            </Dimmer>
        );
    };

    renderReloadPage = (activated: boolean, message: ReloadMessage) => {
        if (activated) {
            return <ReloadPage active={activated} message={message} />;
        }
        return null;
    };

    newGradingToolNotActivated = (): React.ReactElement => {
        const { context } = this.props;
        setTimeout(() => { navigationListService.backToOriginalTool(context); }, 3000);
        return (
            <React.Fragment>
                <div
                    className={styles.notActivated}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(tl('grading.newGradingToolNotActivated')) }}
                />
                <div className={styles.redirectText}>
                    {tl('grading.redirect')}
                </div>
            </React.Fragment>
        );
    };

    handleImpersonationExit = async () => {
        const { session, onPushAlert } = this.props;
        const { originalUser } = session;

        if (!originalUser) return;

        try {
            await apiService.endImpersonation();
            const sessionData: ExtendedSessionState = {
                ...session,
                user: originalUser,
                originalUser: null,
                impersonationType: null,
            };
            this.props.onSetSession(sessionData);
            sessionStorage.removeItem('impersonationData');
            const rootURL = urlUtil.getRootUrlFromLocation(location);
            window.location.href = rootURL.concat('/admin#home');
        } catch {
            onPushAlert({
                key: 'impersonation.exitErrorTitle',
                info: 'impersonation.exitErrorInfo',
                level: AlertLevel.ERROR,
            }, true);
        }
    };

    getExpandedFeatureType = (): string => {
        const { genericFeatureState } = this.props.workspaces;
        return Object
            .keys(genericFeatureState)
            .find((key) => genericFeatureState[key].isExpanded);
    };

    getGradeTypeValues = (): GradeType => (gradingAppStore.getState() as CommonRootState).assessment?.gradeTypeValues;

    getAssessmentState = (): AssessmentState => (gradingAppStore.getState() as RootState).assessment;

    getGradeAsString = (value: number) => {
        const gradeTypeValues = this.getGradeTypeValues();
        const keys = Object.keys(gradeTypeValues);
        return keys.find((char: string) => gradeTypeValues[char] === value);
    }

    replaceGradeInPayload = (payload: any) => {
        const { gradeType } = this.getAssessmentState();
        if (payload && payload.grade) {
            const clonedPayload = { ...payload };
            const newGrade = this.getGradeAsString((+clonedPayload.grade));
            clonedPayload.grade = gradeType === 'NormalizedGradingScale' ? payload.normalizedGrade : getGradeTranslatedLabel(newGrade);
            return clonedPayload;
        }
        return payload;
    }

    renderAlerts = (): React.ReactElement[] => {
        const { alerts } = this.props.alerts;
        return alerts.map((alert: Alert) => {
            const {
                key, info, level, payload, link, icon, loading, disregardAPINameInCaption, api, instructions,
            } = alert;
            let alertTitle = key;
            let alertMessage = info;
            if (!isNullOrUndefined(disregardAPINameInCaption) && !disregardAPINameInCaption) {
                alertTitle = `server.${api}.${key}.title`;
                alertMessage = `server.${api}.${key}.message`;
            }
            return (
                <NotificationDisplay
                    appStore={gradingAppStore.getStore()}
                    key={`notification_${key}_${info}`}
                    alertTitleCaption={alertTitle}
                    alertMessageCaption={alertMessage}
                    alertMessageInstructions={instructions}
                    level={level}
                    payload={payload}
                    link={link}
                    icon={icon}
                    loading={loading}
                    manipulatePayload={this.replaceGradeInPayload}
                />
            );
        });
    };

    renderAuthError = (): React.ReactElement => {
        const { context } = this.props;
        setTimeout(() => { navigationListService.backToOriginalTool(context); }, 3000);
        return (
            <ErrorPage
                errorHeader={tl('grading.authentication.denied')}
            />
        );
    };

    renderCustomHeader = (): React.ReactElement => {
        const {
            workspaces, context, onSetupContextReducer, onFeatureLoadComplete, session,
        } = this.props;
        const activeOverlay = workspaces && workspaces.activeOverlay;
        const featureId = workspaces?.currentWorkspace?.header?.type;
        return (
            <FeatureLoader
                FeatureHeader={FeatureHeader}
                setupGenericFeatureState={onSetupContextReducer}
                getGenericFeatureState={this.onGetGenericFeatureState}
                isFeatureLoading={this.isFeatureLoading}
                setFeatureLoaded={onFeatureLoadComplete}
                featureName={featureId}
                options={null}
                context={context}
                featureId={featureId}
                workspaces={workspaces}
                language={session?.language}
                activeOverlay={activeOverlay}
                appStore={gradingAppStore.getStore()}
                featureNamespace={getFeatureNamespace(featureId, FeatureNamespace.GRADING)}
                actionStatusService={actionStatusService}
                disableFeatureWrapperStyles
            />
        );
    }

    renderFooter = (showFooterInHeader: boolean): React.ReactElement => {
        const {
            context, workspaces, session, onSetupContextReducer, onFeatureLoadComplete,
        } = this.props;
        const { currentWorkspace, activeOverlay } = workspaces;

        return (
            <FeatureLoader
                footerMode
                upwardDropdown={showFooterInHeader}
                FeatureHeader={FeatureHeader}
                setupGenericFeatureState={onSetupContextReducer}
                getGenericFeatureState={this.onGetGenericFeatureState}
                isFeatureLoading={this.isFeatureLoading}
                setFeatureLoaded={onFeatureLoadComplete}
                featureName="footer"
                options={currentWorkspace.footer.options}
                context={context}
                featureId={`feature_footer_${context.workspaceId}`}
                workspaces={workspaces}
                language={session?.language}
                activeOverlay={activeOverlay}
                appStore={gradingAppStore.getStore()}
                featureNamespace={getFeatureNamespace('footer', FeatureNamespace.GRADING)}
                actionStatusService={actionStatusService}
                key={`footer-${context.workspaceId}`}
            />
        );
    }

    render() {
        const {
            context,
            session,
            workspaces,
            onSetupContextReducer,
            onFeatureLoadComplete,
            assessment,
            reload,
            alerts,
            optionsMenu,
            shouldShowRecordingHeader,
        } = this.props;
        const isInFocusMode = workspaces.focusMode || isFocusView();
        const printMode = workspaces.printMode || isPrintViewParams();
        const { activeOverlay } = workspaces;
        const { workspaceId, showDetailsPanel } = context;
        if (!assessment.assessmentRunTitle) {
            if (assessment.authenticationError) {
                return this.renderAuthError();
            }

            return this.renderLoader();
        }

        if (isAiMarkingViewTab()) {
            return <AiMarkingFullWindow />;
        }

        if (!assessment.useNewGradingTool && !printMode && !isInFocusMode) {
            return this.newGradingToolNotActivated();
        }
        const expandedFeatureId: string = this.getExpandedFeatureType();
        const aFeatureIsExpanded = !!expandedFeatureId;
        const showReload: boolean = reload && reload.showReload;
        const { workspaceOverlay, currentWorkspace, workspaceScheme } = workspaces;
        const workspaceIsVisible = workspaceScheme
            && commonWorkspaceSchemeService.isWorkspaceVisible(workspaceScheme.workspaceGroups, context.workspaceId);
        const alertsMountNode: Node = document.getElementById('alertBoxTarget');
        const printAnnotationsWorkspaceMode: boolean = workspaceId === PRINT_ANNOTATIONS_ONLY_WORKSPACE
            && printMode && isInFocusMode;
        const workspaceOverlayProps = {
            appStore: gradingAppStore.getStore(),
            actionStatusService,
            urlContextParametersService,
        };
        const shouldShowWorkspacesSidebar = !isInFocusMode && !aFeatureIsExpanded
            && session?.user?.privileges
            && workspaces?.workspaceScheme?.workspaceGroups.length > 0
            && workspaceScheme.schemeId !== 'ielts_speaking_scheme';
        const showFooterInHeader = currentWorkspace?.workspaceHeader?.navigationInHeader;

        const renderMainContent = () => {
            const candidateCount = getCandidateCountBySelectedCommitteeId(context.selectedCommitteeId);

            if (!assessment.videoBasedOralExam && !candidateCount && !userIsStudent() && workspaceId !== 'plan_workspace_visibility') {
                return (
                    <ErrorPage
                        errorHeader={tl('grading.noSubmission.header')}
                        icon="search"
                        errorContent={tl('grading.noSubmission.errorContent')}
                        iconSize="big"
                    />
                );
            }

            const headerFeatureName = currentWorkspace?.workspaceHeader?.customHeaderFeature || 'workspaceHeader';
            const headerWidgets = context.showDetailedView ? [] : (currentWorkspace?.workspaceHeader?.headerWidgets || []);
            const enableWorkspaceBreadcrumbs = currentWorkspace?.workspaceHeader?.enableWorkspaceBreadcrumbs;

            return (!workspaceIsVisible && !printAnnotationsWorkspaceMode)
                ? (
                    <div className={styles.workspaceLayout}>
                        <div className="column column--flex-12">
                            <FeatureLoader
                                FeatureHeader={FeatureHeader}
                                headerMode={false}
                                footerMode={false}
                                setupGenericFeatureState={onSetupContextReducer}
                                getGenericFeatureState={this.onGetGenericFeatureState}
                                isFeatureLoading={this.isFeatureLoading}
                                setFeatureLoaded={onFeatureLoadComplete}
                                featureName="workspaceDisabled"
                                options={{}}
                                context={context}
                                featureId={`feature_workspaceDisabled_${context.workspaceId}`}
                                language={session?.language}
                                activeOverlay={activeOverlay}
                                appStore={gradingAppStore.getStore()}
                                featureNamespace={getFeatureNamespace('workspaceDisabled', FeatureNamespace.GRADING)}
                                appName={FeatureNamespace.GRADING}
                                actionStatusService={actionStatusService}
                            />
                        </div>
                    </div>
                )
                : (
                    <React.Fragment>
                        {!!currentWorkspace
                        && !!currentWorkspace.workspaceHeader
                        && !!currentWorkspace.workspaceHeader.showHeader
                            ? (
                                <>
                                    <FeatureLoader
                                        FeatureHeader={FeatureHeader}
                                        headerMode
                                        footerMode={false}
                                        setupGenericFeatureState={onSetupContextReducer}
                                        getGenericFeatureState={this.onGetGenericFeatureState}
                                        isFeatureLoading={this.isFeatureLoading}
                                        setFeatureLoaded={onFeatureLoadComplete}
                                        featureName={headerFeatureName}
                                        options={
                                            workspaces.currentWorkspace
                                            && workspaces.currentWorkspace.workspaceHeader.options
                                        }
                                        context={context}
                                        featureId={`feature_workspaceHeader_${context.workspaceId}`}
                                        language={session?.language}
                                        activeOverlay={activeOverlay}
                                        appStore={gradingAppStore.getStore()}
                                        featureNamespace={getFeatureNamespace('workspaceHeader', FeatureNamespace.GRADING)}
                                        actionStatusService={actionStatusService}
                                        headerWidgets={headerWidgets}
                                        enableWorkspaceBreadcrumbs={enableWorkspaceBreadcrumbs}
                                    />
                                    {(currentWorkspace && workspaceParser.isShowFooter(currentWorkspace.footer) && showFooterInHeader)
                                    && !printMode
                                    && (this.renderFooter(showFooterInHeader))}
                                </>
                            )
                            : null}
                        <div className={styles.workspaceLayout}>
                            {!!workspaces.currentWorkspace && aFeatureIsExpanded
                                && this.renderOnlyExpandedFeature(expandedFeatureId)}
                            {!!workspaces.currentWorkspace && !aFeatureIsExpanded
                                && (
                                    <ReflexContainer orientation="vertical">
                                        {this.parseFeatures(aFeatureIsExpanded)}
                                    </ReflexContainer>
                                )}
                        </div>
                    </React.Fragment>
                );
        };
        return (
            <div className={styles.appContainer}>
                {this.props.session.originalUser && <ImpersonationBanner onExit={this.handleImpersonationExit} />}
                <div
                    className={`${styles.wrapper} ${showReload ? styles.blur : ''} ${showDetailsPanel ? styles.wrapperWithDetailsPanelOpen : ''}`}
                    lang={session?.language}
                    id={WorkspacesOverlayTargets.APP_OVERLAY_TARGET}
                >
                    {this.renderReloadPage(showReload, reload.message)}
                    {this.getDevEnvironmentConfig()}
                    {workspaceOverlay && <WorkspaceOverlay {...workspaceOverlayProps} />}
                    {!isInFocusMode && !shouldShowRecordingHeader && currentWorkspace
                        ? currentWorkspace?.header?.type === 'speakingTestHeader'
                            ? this.renderCustomHeader() : <Header />
                        : null}
                    <WarningBar />
                    {!isInFocusMode && shouldShowRecordingHeader && this.renderExplanationRecordingHeaderFeature()}
                    <div className={styles.gradingBody}>
                        { shouldShowWorkspacesSidebar && (
                            <Sidebar
                                context={context}
                                mode="workspaces"
                                workspaces={workspaces}
                                committees={assessment?.committees}
                            />
                        )}
                        <div className={styles.workspaceWrapper}>
                            <div
                                id={WorkspacesOverlayTargets.WORKSPACE_OVERLAY_TARGET}
                                className={workspaceOverlay ? styles.overlayContainer : ''}
                            />
                            <div className={styles.mainContainer} role="main">
                                {renderMainContent()}
                            </div>
                        </div>
                    </div>
                    {(currentWorkspace && workspaceParser.isShowFooter(currentWorkspace.footer) && !showFooterInHeader)
                    && !printMode
                    && (this.renderFooter(showFooterInHeader))}
                    {alerts.alerts.length && alertsMountNode
                        ? (
                            <Portal
                                open
                                mountNode={alertsMountNode}
                            >
                                <>{this.renderAlerts()}</>
                            </Portal>
                        )
                        : null}
                    <ModalDialog />
                    { /* AI-supported marking intro modal always mounted */ }
                    <AiSupportedMarkingIntroModal />
                    {/* Add this because we use a lot of iframe. Which will destroy the popup close when click outside */}
                    {optionsMenu.isMenuOpen && <div className={styles.transparentOverlay} />}
                </div>
                { showDetailsPanel
                    && (
                        <div className={styles.detailsPanelWrapper}>
                            <FeatureLoader
                                FeatureHeader={FeatureHeader}
                                setupGenericFeatureState={onSetupContextReducer}
                                getGenericFeatureState={this.onGetGenericFeatureState}
                                isFeatureLoading={this.isFeatureLoading}
                                setFeatureLoaded={onFeatureLoadComplete}
                                featureName="detailsPanel"
                                options={null}
                                context={context}
                                featureId="feature_detailsPanel"
                                workspaces={workspaces}
                                language={session?.language}
                                activeOverlay={activeOverlay}
                                appStore={gradingAppStore.getStore()}
                                featureNamespace={getFeatureNamespace('detailsPanel', FeatureNamespace.GRADING)}
                                actionStatusService={actionStatusService}
                            />
                        </div>
                    )}
            </div>
        );
    }
}

const mapStateToProps = (state: RootState): StateProps => ({
    context: state.context,
    session: state.session,
    workspaces: state.workspaces,
    assessment: state.assessment,
    reload: state.reload,
    alerts: state.alerts,
    optionsMenu: state.optionsMenu,
    shouldShowRecordingHeader: recordingService.shouldShowRecordingHeader(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators(
    {
        onFetchSignedWebSocketUrl: sessionActions.fetchSignedWebSocketUrl,
        onSetupContextReducer: workspaceActions.setupGenericFeatureState,
        onFeatureLoadComplete: workspaceActions.featureLoaded,
        onSetSession: sessionActions.setSessionData,
        onPushAlert: alertActions.pushAlert,
    },
    dispatch,
);

export default connect<StateProps>(mapStateToProps, mapDispatchToProps)(AppComponent);
